import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { setGrowthUser } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth relies on the httpOnly `access_token` cookie set by the backend —
// no tokens are kept in localStorage (XSS-safe).
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("signup"); // signup | login | forgot
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const [postAuthRedirect, setPostAuthRedirect] = useState(null);

  useEffect(() => { setGrowthUser(user?.id); }, [user]);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth (#session_id=...), AuthCallback exchanges it first.
    if (window.location.hash?.includes("session_id=")) return;
    fetchMe();
  }, [fetchMe]);

  const googleSession = useCallback(async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async ({ email, password, full_name }) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, full_name });
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    await axios.post(`${API}/auth/forgot-password`, { email });
  }, []);

  const resetPassword = useCallback(async ({ email, code, new_password }) => {
    const { data } = await axios.post(`${API}/auth/reset-password`, {
      email, code, new_password,
    });
    setUser(data.user);
    return data.user;
  }, []);

  const signAgreement = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/auth/sign-agreement`, payload);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout request failed (clearing session anyway):", error);
    }
    setUser(null);
  }, []);

  const openAuth = useCallback((mode = "signup", redirectTo = null) => {
    setAuthModalMode(mode);
    if (redirectTo) setPostAuthRedirect(redirectTo);
    setAuthModalOpen(true);
  }, []);

  /**
   * Auth-only gate (assessments require sign-up + agreement)
   */
  const requireAuth = useCallback((redirectTo) => {
    if (user && user.agreement_signed) return true;
    setPostAuthRedirect(redirectTo || null);
    if (!user) {
      setAuthModalMode("signup");
      setAuthModalOpen(true);
    } else {
      setAgreementModalOpen(true);
    }
    return false;
  }, [user]);

  /**
   * Premium gate — requires auth + agreement + active premium subscription
   */
  const requirePremium = useCallback((redirectTo) => {
    if (!requireAuth(redirectTo)) return false;
    if (user?.is_premium) return true;
    setPostAuthRedirect(redirectTo || null);
    setPaywallModalOpen(true);
    return false;
  }, [user, requireAuth]);

  const startCheckout = useCallback(async (packageId = "monthly_premium") => {
    const { data } = await axios.post(`${API}/payments/checkout`, {
      package_id: packageId,
      origin_url: window.location.origin,
    });
    return data; // { url, session_id }
  }, []);

  const subscribe = useCallback(async () => {
    const { data } = await axios.post(`${API}/payments/subscribe`, {
      origin_url: window.location.origin,
    });
    return data; // { url, session_id }
  }, []);

  const cancelSubscription = useCallback(async () => {
    const { data } = await axios.post(`${API}/payments/cancel-subscription`);
    return data;
  }, []);

  const value = {
    user,
    loading: user === undefined,
    isAuthenticated: !!user,
    hasSignedAgreement: !!user?.agreement_signed,
    isPremium: !!user?.is_premium,
    register, login, requestPasswordReset, resetPassword,
    signAgreement, logout, fetchMe, googleSession,
    authModalOpen, setAuthModalOpen,
    authModalMode, setAuthModalMode,
    agreementModalOpen, setAgreementModalOpen,
    paywallModalOpen, setPaywallModalOpen,
    requireAuth, requirePremium,
    openAuth, startCheckout, subscribe, cancelSubscription,
    postAuthRedirect, setPostAuthRedirect,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
